
import { Card, CardContent } from "@/components/ui/card";
import { Package } from "lucide-react";
import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <Package className="h-8 w-8 text-purple-600" />
            <span className="text-2xl font-bold text-gray-900">OrderEaseOnline</span>
          </Link>
          <Link to="/" className="text-purple-600 hover:text-purple-700">
            Back to Home
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>
              <p className="text-gray-600 mb-8">Last updated: January 1, 2024</p>

              {/* Etsy Attribution Notice */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8">
                <p className="text-orange-800 font-medium">
                  The term 'Etsy' is a trademark of Etsy, Inc. This application uses the Etsy API but is not endorsed or certified by Etsy, Inc.
                </p>
              </div>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptance of Terms</h2>
                  <p className="text-gray-700 mb-4">
                    By accessing and using OrderEaseOnline, you accept and agree to be bound by the terms 
                    and provision of this agreement. If you do not agree to abide by the above, 
                    please do not use this service.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Use License</h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="mb-4">
                      Permission is granted to temporarily download one copy of OrderEaseOnline per device 
                      for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                    </p>
                    <ul className="list-disc list-inside mb-4 space-y-2">
                      <li>modify or copy the materials</li>
                      <li>use the materials for any commercial purpose or for any public display</li>
                      <li>attempt to reverse engineer any software contained on the website</li>
                      <li>remove any copyright or other proprietary notations from the materials</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Service Description</h2>
                  <p className="text-gray-700 mb-4">
                    OrderEaseOnline provides order management tools for Etsy sellers. Our service includes 
                    order tracking, analytics, customer management, and integration with Etsy's platform 
                    through the Etsy API. The term 'Etsy' is a trademark of Etsy, Inc. This application uses 
                    the Etsy API but is not endorsed or certified by Etsy, Inc. We reserve the right to modify 
                    or discontinue any aspect of our service at any time.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">User Accounts</h2>
                  <p className="text-gray-700 mb-4">
                    When you create an account with us, you must provide information that is accurate, 
                    complete, and current at all times. You are responsible for safeguarding the password 
                    and for all activities that occur under your account.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Payment Terms and Refund Policy</h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="mb-4">Subscription fees are billed in advance on a monthly or annual basis. You agree to:</p>
                    <ul className="list-disc list-inside mb-4 space-y-2">
                      <li>Pay all charges at the prices in effect when incurred</li>
                      <li>Pay any applicable taxes</li>
                      <li>Be responsible for all charges incurred under your account</li>
                    </ul>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <h3 className="font-semibold text-red-800 mb-2">No Refund Policy</h3>
                      <p className="text-red-700">
                        All subscription fees are non-refundable. We do not provide refunds or credits for any unused subscription time, 
                        regardless of the reason for cancellation. This policy applies to all circumstances, including but not limited to 
                        dissatisfaction with the service, technical issues, or early termination of your subscription.
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Subscription Cancellation</h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="mb-4">
                      You may cancel your subscription at any time through your account settings or by contacting our support team. 
                      Please note the following cancellation terms:
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <h3 className="font-semibold text-yellow-800 mb-2">Immediate Cancellation Policy</h3>
                      <p className="text-yellow-700 mb-2">
                        When you cancel your subscription, your access to premium features will be terminated immediately. 
                        You will not retain access to paid features until your next billing date.
                      </p>
                      <p className="text-yellow-700">
                        Your account will revert to any available free tier immediately upon cancellation, and you will lose 
                        access to all premium functionality without compensation for the remaining subscription period.
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
                  <p className="text-gray-700 mb-4">
                    In no event shall OrderEaseOnline or its suppliers be liable for any damages (including, 
                    without limitation, damages for loss of data or profit, or due to business interruption) 
                    arising out of the use or inability to use OrderEaseOnline, even if OrderEaseOnline or an authorized 
                    representative has been notified orally or in writing of the possibility of such damage.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law</h2>
                  <p className="text-gray-700 mb-4">
                    These terms and conditions are governed by and construed in accordance with the laws 
                    of the United States and you irrevocably submit to the exclusive jurisdiction of the 
                    courts in that state or location.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                  <p className="text-gray-700">
                    If you have any questions about these Terms of Service, please contact us at{" "}
                    <a href="mailto:support@ordereaseonline.com" className="text-purple-600 hover:underline">
                      support@ordereaseonline.com
                    </a>
                  </p>
                </section>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Terms;
